import type { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import type { AdaptationType } from "@/types/adaptation";

export interface AssetCacheParams {
  schema: Pick<PortfolioExportJobSchema, "assets">;
  /** Live key count of the asset store. */
  cachedCount: number;
}

/**
 * Whether the cached portfolio can be served without re-pulling the asset inputs.
 */
export const isAssetCacheComplete = ({ schema, cachedCount }: AssetCacheParams): boolean => {
  const { processedCount, unprocessedCount } = schema.assets;

  if (processedCount === 0 || cachedCount === 0) return false;

  if (unprocessedCount > 0) return false;

  return cachedCount === processedCount;
};

/**
 * The number of asset input rows the portfolio can return (used for paging).
 */
export const assetInputRowCount = (schema: Pick<PortfolioExportJobSchema, "assets">): number =>
  Math.max(0, schema.assets.totalCount - schema.assets.errorCount);

/** One adaptation run to load, with the counts its `adaptations` row reports. */
export interface AdaptationRun {
  id: string;
  adaptationType: AdaptationType;
  /** Pathway actions the run finished. Always 0 for `baseline`; see below. */
  processedCount: number;
  /** Pathway actions the run has yet to finish. */
  unprocessedCount: number;
}

export interface AdaptationCacheParams {
  schema: Pick<PortfolioExportJobSchema, "assets">;
  runs: readonly AdaptationRun[];
  /** Live key count of the adaptation store. */
  cachedCount: number;
}

/**
 * How many adaptation records a fully cached portfolio holds.
 *
 * The baseline run is the one case that never streams: its actions re-use the
 * asset analyses unchanged, so nothing reports progress against its row and its
 * counts stay null. Its share is however many assets produced a result — which
 * is also exactly what `AssetDataLoader` mirrors into the adaptation store.
 */
export const adaptationActionCount = ({ schema, runs }: Omit<AdaptationCacheParams, "cachedCount">): number =>
  runs.reduce(
    (sum, run) => sum + (run.adaptationType === "baseline" ? schema.assets.processedCount : run.processedCount),
    0
  );

/**
 * The store size when every run holds one action per analysed asset, as an auto
 * run does. Unlike the run counts, the asset counts are the portfolio's own.
 */
export const expectedAdaptationCount = ({ schema, runs }: Omit<AdaptationCacheParams, "cachedCount">): number =>
  schema.assets.processedCount * runs.length;

/**
 * Whether the cached adaptations can be served without re-pulling the actions.
 */
export const isAdaptationCacheComplete = ({ schema, runs, cachedCount }: AdaptationCacheParams): boolean => {
  if (runs.length === 0 || cachedCount === 0) return false;

  // Checked first: a run's counts are only as good as the tab that wrote them back
  if (schema.assets.unprocessedCount === 0 && cachedCount === expectedAdaptationCount({ schema, runs })) return true;

  if (runs.some((run) => run.unprocessedCount > 0)) return false;

  // A run that finished before its row was ever written back reports zero, which
  // reads the same as a run that produced nothing. Re-read the actions rather
  // than risk serving a portfolio that is silently missing a whole run.
  if (runs.some((run) => run.adaptationType !== "baseline" && run.processedCount === 0)) return false;

  const expectedCount = adaptationActionCount({ schema, runs });

  return expectedCount > 0 && cachedCount === expectedCount;
};

/**
 * What a load records once it has checked the store against the actions
 * Supabase lists, so later loads can trust the store without asking again.
 */
export interface VerifiedAdaptationCache {
  runsKey: string;
  cachedCount: number;
}

/** Identifies the runs and the counts they reported when the store was verified. */
export const adaptationRunsKey = (runs: readonly AdaptationRun[]): string =>
  runs
    .map((run) => `${run.id}:${run.processedCount}:${run.unprocessedCount}`)
    .sort()
    .join(",");

/**
 * Whether the store is still as the last verification left it.
 *
 * Covers what `isAdaptationCacheComplete` cannot count its way to: a store
 * short of actions that will never produce a result.
 */
export const isAdaptationCacheVerified = ({
  runs,
  cachedCount,
  verified,
}: {
  runs: readonly AdaptationRun[];
  cachedCount: number;
  verified: VerifiedAdaptationCache | null;
}): boolean =>
  verified !== null &&
  runs.length > 0 &&
  cachedCount > 0 &&
  verified.cachedCount === cachedCount &&
  verified.runsKey === adaptationRunsKey(runs);

// whether a run finished processing. adaptations with pending analysis will be false
export const isAdaptationRunComplete = (run: AdaptationRun | undefined): run is AdaptationRun =>
  run !== undefined && run.unprocessedCount === 0 && run.processedCount > 0;
